<template>
  <div class="publisher-wrapper">
    <video v-if="showPreview" ref="localVideo" autoplay muted playsinline class="local-video"></video>
    <canvas v-if="showPreview" ref="detectionCanvas" class="detection-overlay"></canvas>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useObjectDetection } from '@/composables/useObjectDetection'
import { DronecastClient } from '../core/dronecastClient'
import { IDCMessage, TDCCastroomId, TDCPayloadCastStreamResult, TDCPayloadCreateCastroomResult, TDCPayloadExchangeIcecandidate, TDCPayloadCastroomStatusChanged, TDCPayloadViewerListChanged, MediaServerType } from '../core/clientInterfaces'
import { monitoringService } from '@/services/monitoring.service'

type KurentoUtilsLike = {
  WebRtcPeerSendonly: new (options: any, cb: (err?: any) => void) => any
  dispose: (peer: any) => void
}

const props = defineProps<{
  castroomId: TDCCastroomId
  wsUrl: string
  kurentoUtils: KurentoUtilsLike
  source?: 'webcam' | 'screen'
  showPreview?: boolean
  constraints?: MediaStreamConstraints
  mediaServerType?: MediaServerType
  detectionEnabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'onCastroomStatusChanged', result: {castroomId: any, status: string, reason?: 'caster_stopped' | 'admin_closed' | 'error'}): void
  (e: 'onWsStateChanged', state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected'): void
  (e: 'onWebRtcStateChanged', role: 'caster', connectionState?: string, iceState?: string): void
  (e: 'onViewerListChanged', viewerList: string[]): void
}>()

defineExpose({
  cast: (overrideCastroomId?: TDCCastroomId) => {
    if (overrideCastroomId) {
      overrideId = { gcs: overrideCastroomId.gcs, drone: overrideCastroomId.drone, camera: overrideCastroomId.camera }
    }
    initClient()
    createCastroom()
  },
  stop: () => {
    stopPublishing()
  }
})

const showPreview = ref<boolean>(props.showPreview ?? false)
const localVideo = ref<HTMLVideoElement | null>(null)
const detectionCanvas = ref<HTMLCanvasElement | null>(null)
let webRtcPeer: any | null = null

// Object Detection
const detectionEnabledRef = computed(() => !!props.detectionEnabled)
useObjectDetection(localVideo, detectionCanvas, detectionEnabledRef)
let iceCandidateQueue: RTCIceCandidate[] = []
let client: DronecastClient | null = null
let localStream: MediaStream | null = null
let localStreamSource: 'webcam' | 'screen' | null = null  // 현재 스트림의 소스 타입
let overrideId: TDCCastroomId | null = null
let publishingStarted = false

// showPreview일 때 마운트 시 바로 카메라 미리보기 시작
onMounted(async () => {
  if (showPreview.value) {
    await openPreviewStream()
  }
})

/** 카메라/화면 스트림을 획득하여 localVideo에 표시 */
async function openPreviewStream() {
  const src = props.source || 'webcam'
  // 소스 타입이 동일한 스트림이 이미 열려있으면 무시
  if (localStream && localStreamSource === src) return
  // 소스 타입이 변경되었으면 기존 스트림 해제
  if (localStream) {
    localStream.getTracks().forEach(t => t.stop())
    localStream = null
    localStreamSource = null
  }
  try {
    if (src === 'webcam') {
      // 해상도·프레임을 명시해 캡처를 결정적으로 만든다(무제약 video:true 는 기기별로
      // 저해상도를 고를 수 있다). props.constraints 로 상위에서 덮어쓸 수 있다.
      const constraints = props.constraints || {
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        audio: true,
      }
      try {
        localStream = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (e) {
        // mic가 없는 디바이스 등에서 오디오 획득 실패 시 audio 없이 재시도
        console.warn('[DronecastCaster] audio 포함 스트림 획득 실패, audio 없이 재시도:', e)
        localStream = await navigator.mediaDevices.getUserMedia({ ...constraints, audio: false })
      }
    } else {
      localStream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: true })
    }
    localStreamSource = src
    if (localVideo.value) {
      localVideo.value.srcObject = localStream
    }
  } catch (err) {
    console.error('[DronecastCaster] 미리보기 스트림 획득 실패:', err)
  }
}

function initClient() {
  if (client) {
    client.close()
    client = null
  }
  client = new DronecastClient('caster')
  client.onWsStateChange = (state) => emit('onWsStateChanged', state)
  client.connect(props.wsUrl)
  client.setMessageHandler('create_castroom_result', onCreateCastroomResult)
  client.setMessageHandler('cast_stream_result', onCastStreamResult)
  client.setMessageHandler('exchange_icecandidate', onExchangeIcecandidate)
  client.setMessageHandler('castroom_status_changed', onCastroomStatusChanged)
  client.setMessageHandler('viewer_list_changed', onViewerListChanged)
  // BL-604: 서버 push 미디어 이벤트 → monitoring store 반영
  client.setMessageHandler('evt.media.state_changed', (msg) => {
    monitoringService.handleServerMediaStateChanged(msg.payload as any)
  })
  client.setMessageHandler('evt.media.quality_report', (msg) => {
    monitoringService.handleServerMediaQualityReport(msg.payload as any)
  })
}

function createCastroom() {
  const id = overrideId || { gcs: props.castroomId?.gcs, drone: props.castroomId?.drone, camera: props.castroomId?.camera }
  const sourceType = (props.source || 'webcam') === 'screen' ? 'browser_screen' : 'browser_camera'
  client?.createCastroom(id as TDCCastroomId, {
    mediaServerType: props.mediaServerType,
    sourceType,
  })
}

async function onCreateCastroomResult(message: IDCMessage) {
  const { result, error } = message.payload as TDCPayloadCreateCastroomResult
  console.log(`[DronecastCaster] create_castroom_result: result=${result}, publishingStarted=${publishingStarted}`)
  if (result === 'success') {
    if (publishingStarted) {
      console.log('[DronecastCaster] 중복 create_castroom_result 무시')
      return
    }
    publishingStarted = true
    await startPublishing()
  } else {
    console.error('[DronecastCaster] create_castroom_result error:', error)
  }
}

// 송출 비트레이트 목표(kbps). relay(libdatachannel)는 transport-cc·REMB 를 SDP 로는
// 협상하지만 실제 RTCP 피드백 패킷을 보내지 않는다(2026-09 실측). 그러면 Chrome 송신
// BWE 는 피드백을 손실로 간주해 최저치(~37kbps)로 붕괴한다. b=AS·setParameters(maxBitrate)는
// '천장'이라 붕괴한 추정치를 못 올린다 — Chrome 전용 fmtp x-google-min-bitrate 로 '바닥'을
// 박아야 BWE 와 무관하게 최소 비트레이트가 유지된다.
const VIDEO_MIN_KBPS = 1500
const VIDEO_START_KBPS = 2500
const VIDEO_MAX_KBPS = 3500

// 비디오 섹션을 이렇게 손본다:
//   (1) transport-cc·goog-remb 피드백과 transport-wide-cc 확장을 제거 → Chrome 송신 BWE
//       컨트롤러 자체를 끈다. relay 가 피드백 RTCP 를 안 보내므로 BWE 가 켜져 있으면 이를
//       손실로 오인해 바닥으로 끌어내린다(x-google 하한마저 무시). 컨트롤러를 끄면 인코더
//       목표치로 고정 송신한다. nack/pli/fir(재전송·키프레임)는 유지.
//   (2) b=AS/b=TIAS 상한 힌트.
//   (3) 각 코덱 fmtp 에 x-google-*-bitrate 하한/시작/상한.
function patchVideoSdp(sdp: string): string {
  const lines = sdp.split(/\r?\n/)
  const out: string[] = []
  let inVideo = false
  let hasBw = false
  for (let line of lines) {
    if (line.startsWith('m=')) { inVideo = line.startsWith('m=video'); hasBw = false }
    if (inVideo) {
      // 송신 BWE 를 완전히 끈다 — relay 가 피드백 RTCP(transport-cc·REMB)를 안 보내므로
      // 어떤 추정기든 켜져 있으면 시작값에서 못 올라가거나 바닥으로 붕괴한다.
      // transport-cc·goog-remb 피드백, transport-wide-cc·abs-send-time 확장을 모두 제거.
      if (/a=rtcp-fb:\S+ (transport-cc|goog-remb)/.test(line)) continue
      if (/a=extmap:\d+ .*(transport-wide-cc|abs-send-time)/.test(line)) continue
    }
    if (inVideo && line.startsWith('b=')) hasBw = true
    // c= 다음에 b=AS/b=TIAS 삽입
    if (inVideo && line.startsWith('c=') && !hasBw) {
      out.push(line)
      out.push(`b=AS:${VIDEO_MAX_KBPS}`)
      out.push(`b=TIAS:${VIDEO_MAX_KBPS * 1000}`)
      hasBw = true
      continue
    }
    // 비디오 코덱 fmtp 에 Chrome 인코더 비트레이트 하한/시작/상한 강제
    if (inVideo && line.startsWith('a=fmtp:') && !line.includes('x-google-min-bitrate')) {
      line = `${line};x-google-min-bitrate=${VIDEO_MIN_KBPS};x-google-start-bitrate=${VIDEO_START_KBPS};x-google-max-bitrate=${VIDEO_MAX_KBPS}`
    }
    out.push(line)
  }
  return out.join('\r\n')
}

// 비디오 sender 에 목표/상한 비트레이트를 명시한다. 피드백 없는 relay 에서 인코더가
// 기본 저비트레이트에 머무는 것을 막는다(Python 캐스터의 encodings.maxBitrate 와 동일 취지).
async function forceSenderBitrate(kbps: number) {
  const pc = webRtcPeer?.peerConnection as RTCPeerConnection | undefined
  if (!pc) return
  const sender = pc.getSenders().find(s => s.track?.kind === 'video')
  if (!sender) return
  try {
    const params = sender.getParameters()
    if (!params.encodings || params.encodings.length === 0) params.encodings = [{}]
    params.encodings[0].maxBitrate = kbps * 1000
    ;(params.encodings[0] as any).networkPriority = 'high'
    ;(params as any).degradationPreference = 'maintain-framerate'
    await sender.setParameters(params)
    console.log(`[DronecastCaster] sender maxBitrate=${kbps}kbps 적용`)
  } catch (e) {
    console.warn('[DronecastCaster] setParameters 실패:', e)
  }
}

async function onCastStreamResult(message: IDCMessage) {
  const { result, data, error } = message.payload as TDCPayloadCastStreamResult
  if (result !== 'success' || !data) {
    console.error('cast_stream_result error:', error)
    return
  }
  await webRtcPeer?.processAnswer(patchVideoSdp(data))
  await forceSenderBitrate(VIDEO_MAX_KBPS)
  for (const c of iceCandidateQueue) await webRtcPeer?.addIceCandidate(c)
  iceCandidateQueue = []
}

function onExchangeIcecandidate(message: IDCMessage) {
  const { candidate } = message.payload as TDCPayloadExchangeIcecandidate
  const iceCandidate = candidate as RTCIceCandidate
  // 원격 SDP(answer)가 적용되기 전에는 후보를 큐에 저장했다가 onCast에서 일괄 적용
  if (webRtcPeer && webRtcPeer.peerConnection && webRtcPeer.peerConnection.remoteDescription) {
    webRtcPeer.addIceCandidate(iceCandidate)
  } else {
    iceCandidateQueue.push(iceCandidate)
  }
}

async function startPublishing() {
  const TAG = '[DronecastCaster]'
  console.log(`${TAG} startPublishing: localStream=${!!localStream}`)
  // 프리뷰에서 이미 열린 스트림이 없으면 새로 획득
  if (!localStream) {
    await openPreviewStream()
  }
  if (!localStream) {
    console.error(`${TAG} 미디어 스트림 없음 — getUserMedia 실패`)
    publishingStarted = false
    return
  }
  console.log(`${TAG} 미디어 스트림: tracks=${localStream.getTracks().map(t => `${t.kind}:${t.readyState}`).join(', ')}`)

  // kurento-utils가 인식하는 옵션명은 videoStream / audioStream 이다.
  // mediaStream은 존재하지 않는 옵션이라 무시되고, kurento-utils가 자체적으로
  // getUserMedia({audio:true, video:true})를 호출해버린다. 마이크가 없는 기기
  // (라즈베리파이 등)에서는 그 호출이 NotFoundError로 실패해 송출이 시작되지 않았다.
  // 위에서 이미 오디오 폴백까지 거쳐 얻은 스트림을 그대로 넘겨 재획득을 막는다.
  // videoStream 하나만 넘긴다. kurento-utils는 videoStream/audioStream 각각의
  // getTracks()를 순회하며 addTrack을 호출하므로, 같은 MediaStream을 양쪽에 넘기면
  // 모든 트랙이 두 번 추가되어 "this track is already set on a sender"로 실패한다.
  // localStream에는 오디오·비디오 트랙이 모두 들어 있어 하나로 충분하다.
  const options = {
    localVideo: localVideo.value || undefined,
    videoStream: localStream,
    onicecandidate: async (candidate: RTCIceCandidate) => { await client?.exchangeIceCandidate(candidate) }
  }
  console.log(`${TAG} WebRtcPeerSendonly 생성 중...`)
  webRtcPeer = new props.kurentoUtils.WebRtcPeerSendonly(options, async (err: any) => {
    if (err) { console.error(`${TAG} WebRtcPeerSendonly init error:`, err); return }
    console.log(`${TAG} WebRtcPeer 초기화 완료, offer 생성 중...`)
    const offer = await webRtcPeer?.generateOffer()
    console.log(`${TAG} SDP offer 생성 완료: length=${offer?.length}, hasMedia=${offer?.includes('m=')}`)
    if (offer) client?.castStream(patchVideoSdp(offer))
  })

  if (webRtcPeer?.peerConnection) {
    webRtcPeer.peerConnection.onconnectionstatechange = () => {
      const state = webRtcPeer?.peerConnection?.connectionState
      emit('onWebRtcStateChanged', 'caster', state, undefined)
      // 연결 완료 시점이 sender·encodings 가 확실히 준비된 때다. onCastStreamResult 시점엔
      // 아직 비어 setParameters 가 무시될 수 있으므로 여기서 재적용한다.
      if (state === 'connected') forceSenderBitrate(VIDEO_MAX_KBPS)
    }
    webRtcPeer.peerConnection.oniceconnectionstatechange = () => {
      const iceState = webRtcPeer?.peerConnection?.iceConnectionState
      emit('onWebRtcStateChanged', 'caster', undefined, iceState)
    }
  }
}

function onViewerListChanged(message: IDCMessage) {
  const { viewers } = message.payload as TDCPayloadViewerListChanged
  emit('onViewerListChanged', viewers || [])
}

function onCastroomStatusChanged(message: IDCMessage) {
  const { castroomId, currentStatus, reason } = message.payload as TDCPayloadCastroomStatusChanged
  emit('onCastroomStatusChanged', { castroomId, status: currentStatus, reason })

  if (currentStatus === 'stopped' || currentStatus === 'closed') {
    cleanupLocal()
  }
}

function stopPublishing() {
  // 서버에 캐스트룸 종료 요청
  client?.closeCastroom()
  cleanupLocal()
}

function cleanupLocal() {
  publishingStarted = false
  try {
    webRtcPeer && props.kurentoUtils.dispose(webRtcPeer)
    webRtcPeer = null
    localStream?.getTracks().forEach(t => t.stop())
    localStream = null
    localStreamSource = null
  } catch {}
}

onUnmounted(() => {
  client?.closeCastroom()
  cleanupLocal()
  if (client) {
    client.close()
    client = null
  }
})

</script>

<style scoped>
.publisher-wrapper{position:relative;width:100%;height:100%}
.local-video{width:100%;height:100%;object-fit:contain;display:block}
.detection-overlay{position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none}
</style>


